import { useEffect, useState, type ComponentType } from "react";
import { useHousehold } from "../app/useHousehold";
import { useLayouts, useMe } from "../data/queries";
import { AiSection, CalendarSection, DiagnosticsSection, DisplaySection, HouseholdSection, LocationSection, MembersSection } from "../settings/SettingsPanel";
import { Wordmark } from "../ui/Brand";
import { Icon, type IconName } from "../ui/Icon";
import "./admin.css";
import { AtlasSection } from "./sections/AtlasSection";
import { KitchenSection } from "./sections/KitchenSection";
import { MediaSection } from "./sections/MediaSection";
import { NewsSection } from "./sections/NewsSection";
import { OutdoorsSection } from "./sections/OutdoorsSection";
import { RewardsSection } from "./sections/RewardsSection";
import { RoutinesSection } from "./sections/RoutinesSection";
import { ScreensSection } from "./sections/ScreensSection";
import { TickerSection } from "./sections/TickerSection";

function DisplayWithLayouts() {
  const layouts = useLayouts().data ?? [];
  return <DisplaySection layouts={layouts} />;
}

interface Section {
  id: string;
  label: string;
  icon: IconName;
  group: string;
  Component: ComponentType;
  admin?: boolean;
}

const SECTIONS: Section[] = [
  { id: "family", label: "Family", icon: "user", group: "Household", Component: MembersSection },
  { id: "household", label: "Household & sign-in", icon: "home", group: "Household", Component: HouseholdSection },
  { id: "chores", label: "Chores & rewards", icon: "star", group: "Kids", Component: RewardsSection },
  { id: "routines", label: "Routines", icon: "sun", group: "Kids", Component: RoutinesSection },
  { id: "kitchen", label: "Kitchen & meals", icon: "utensils", group: "Home life", Component: KitchenSection },
  { id: "screens", label: "Screens & schedule", icon: "layout", group: "Display", Component: ScreensSection },
  { id: "ticker", label: "Home ticker", icon: "sparkle", group: "Display", Component: TickerSection },
  { id: "news", label: "News & sports", icon: "news", group: "Display", Component: NewsSection },
  { id: "display", label: "Theme & units", icon: "moon", group: "Display", Component: DisplayWithLayouts },
  { id: "location", label: "Weather location", icon: "pin", group: "Connections", Component: LocationSection },
  { id: "outdoors", label: "Outdoors", icon: "mountain", group: "Connections", Component: OutdoorsSection },
  { id: "calendar", label: "Google Calendar", icon: "calendar", group: "Connections", Component: CalendarSection },
  { id: "atlas", label: "Living Atlas", icon: "globe", group: "Display", Component: AtlasSection },
  { id: "photos", label: "Photo library", icon: "image", group: "Connections", Component: MediaSection, admin: true },
  { id: "ai", label: "AI features", icon: "sparkle", group: "System", Component: AiSection },
  { id: "diagnostics", label: "Diagnostics", icon: "info", group: "System", Component: DiagnosticsSection, admin: true },
];

const fromHash = () => SECTIONS.find((s) => `#${s.id}` === window.location.hash)?.id ?? "family";

/**
 * Full-page admin for configuring the household from any browser (laptop or
 * phone). Same sign-in and permissions as the wall; settings apply everywhere.
 */
export function AdminApp() {
  const me = useMe();
  const { isAdmin, householdName } = useHousehold();
  const [active, setActive] = useState(fromHash);

  useEffect(() => {
    const onHash = () => setActive(fromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const visible = SECTIONS.filter((s) => !s.admin || isAdmin);
  const current = visible.find((s) => s.id === active) ?? visible[0]!;
  const groups = [...new Set(visible.map((s) => s.group))];

  return (
    <div className="admin">
      <header className="admin-top">
        <Wordmark compact />
        <div className="admin-title">
          <strong>{householdName}</strong>
          <span className="muted small">Admin · signed in as {me.data?.member.displayName}</span>
        </div>
        <a className="btn" href="/">
          <Icon name="home" size="1.1rem" /> Back to the display
        </a>
      </header>
      <div className="admin-body">
        <nav className="admin-nav" aria-label="Admin sections">
          {groups.map((g) => (
            <div key={g} className="admin-group">
              <span className="admin-group-label">{g}</span>
              {visible
                .filter((s) => s.group === g)
                .map((s) => (
                  <a key={s.id} href={`#${s.id}`} className={s.id === current.id ? "on" : ""} aria-current={s.id === current.id ? "page" : undefined}>
                    <Icon name={s.icon} size="1.2rem" />
                    {s.label}
                  </a>
                ))}
            </div>
          ))}
        </nav>
        <main className="admin-main">
          {!isAdmin && <p className="banner">You can view these settings; an adult in the household can change them.</p>}
          <div className="admin-card" key={current.id}>
            <current.Component />
          </div>
        </main>
      </div>
    </div>
  );
}
